import { useEffect, useState } from 'react';
import Reveal from '../../components/Reveal';
import HeroSlideshow from '../../components/HeroSlideshow';
import SafeImage from '../../components/SafeImage';
import './safaris.css';
import {
  IMAGE_FALLBACK,
  SAFARI_HERO_SLIDES,
  SAFARI_PAGE_IMAGES,
  SERENGETI_PAGE_IMAGES,
  NGORONGORO_PAGE_IMAGES,
  TARANGIRE_PAGE_IMAGES,
  MIKUMI_PAGE_IMAGES,
  ZANZIBAR_PAGE_IMAGES,
} from '../../data/destinationImages';

const SAFARI_IMAGES = SAFARI_PAGE_IMAGES;

const HERO_STATS = [
  { value: '22', label: 'National parks', delay: 0 },
  { value: '4', label: 'Safari circuits', delay: 80 },
  { value: 'Big 5', label: 'On the Northern Circuit', delay: 160 },
  { value: '100%', label: 'Tailor-made itineraries', delay: 240 },
];

const SAFARI_TYPES = [
  {
    image: SAFARI_IMAGES.gameDrive,
    title: 'Classic Game Drives',
    text: 'Open-roof 4×4 vehicles with licensed guides dawn and dusk drives when wildlife is most active across the savanna.',
  },
  {
    image: SAFARI_IMAGES.balloon,
    title: 'Hot Air Balloon Safaris',
    text: 'Float above the Serengeti at sunrise for a bird\'s-eye view of the plains, followed by a champagne bush breakfast.',
  },
  {
    image: SAFARI_IMAGES.walking,
    title: 'Walking Safaris',
    text: 'Guided bush walks in designated areas track prints, learn flora, and experience the bush at ground level.',
  },
  {
    image: SAFARI_IMAGES.cultural,
    title: 'Cultural Encounters',
    text: 'Visit Maasai bomas, local markets, and community projects for a deeper connection to the people of Tanzania.',
  },
  {
    image: SAFARI_IMAGES.boat,
    title: 'Boat & River Safaris',
    text: 'Cruise the Rufiji River in Nyerere or spot hippos and birds along park waterways a different perspective on wildlife.',
  },
  {
    image: SAFARI_IMAGES.nightDrive,
    title: 'Night & Specialist Drives',
    text: 'Spot nocturnal species on select night drives, or focus on birding, photography, and migration tracking with expert guides.',
  },
];

const SAFARI_CIRCUITS = [
  {
    image: SAFARI_IMAGES.northern,
    title: 'Northern Circuit',
    badge: 'Most popular',
    text: 'The classic Tanzania safari Serengeti, Ngorongoro Crater, Tarangire, Lake Manyara, and Arusha. Ideal for first-time visitors and the Great Migration.',
    parks: [
      { label: 'Serengeti', href: '#serengeti' },
      { label: 'Ngorongoro', href: '#ngorongoro' },
      { label: 'Tarangire', href: '#tarangire' },
    ],
  },
  {
    image: SAFARI_IMAGES.southern,
    title: 'Southern Circuit',
    badge: 'Off the beaten path',
    text: 'Ruaha, Nyerere (Selous), Mikumi, and Udzungwa vast wilderness, fewer crowds, and excellent predator and elephant viewing.',
    parks: [
      { label: 'Mikumi', href: '#mikumi' },
    ],
  },
  {
    image: SAFARI_IMAGES.western,
    title: 'Western Circuit',
    badge: 'Primate & forest',
    text: 'Mahale Mountains and Gombe Stream for chimpanzee trekking, plus Katavi for remote big-game safaris in untouched bush.',
    parks: [],
  },
];

const TRIP_DURATIONS = [
  {
    image: SAFARI_IMAGES.shortTrip,
    title: 'Short Safari',
    duration: '3–4 days',
    text: 'Perfect for a long weekend or add-on Tarangire and Ngorongoro, or a focused Serengeti fly-in from Arusha.',
  },
  {
    image: SAFARI_IMAGES.classicTrip,
    title: 'Classic Safari',
    duration: '5–8 days',
    text: 'The full Northern Circuit experience multiple parks, varied landscapes, and time to settle into the rhythm of the bush.',
  },
  {
    image: SAFARI_IMAGES.extendedTrip,
    title: 'Grand Tanzania Journey',
    duration: '10+ days',
    text: 'Safari, Kilimanjaro or Meru trek, and a Zanzibar beach extension the complete Tanzania adventure in one itinerary.',
  },
];

const INCLUDED_ITEMS = [
  'Licensed, English-speaking safari guide for the full trip',
  'Private 4×4 safari vehicle with pop-up roof and charging points',
  'Park fees, conservation levies, and crater service fees (as quoted)',
  'Accommodation matched to your budget camping, lodge, or luxury tented camp',
  'All meals on safari days and bottled drinking water in the vehicle',
  'Airport transfers and inter-park road or charter flight arrangements',
  '24/7 on-ground support from our Arusha team before and during your trip',
];

const FEATURED_DESTINATIONS = [
  {
    href: '#serengeti',
    image: SERENGETI_PAGE_IMAGES.hero,
    name: 'Serengeti',
    tag: 'Great Migration',
    summary: 'Endless plains and Africa\'s greatest wildlife spectacle.',
  },
  {
    href: '#ngorongoro',
    image: NGORONGORO_PAGE_IMAGES.hero,
    name: 'Ngorongoro',
    tag: 'Crater safari',
    summary: 'A natural amphitheatre packed with Big Five wildlife.',
  },
  {
    href: '#tarangire',
    image: TARANGIRE_PAGE_IMAGES.hero,
    name: 'Tarangire',
    tag: 'Elephant country',
    summary: 'Baobab landscapes and huge elephant herds in dry season.',
  },
  {
    href: '#mikumi',
    image: MIKUMI_PAGE_IMAGES.hero,
    name: 'Mikumi',
    tag: 'Southern gateway',
    summary: 'Accessible savanna wildlife south of Dar es Salaam.',
  },
  {
    href: '#zanzibar',
    image: ZANZIBAR_PAGE_IMAGES.hero,
    name: 'Zanzibar',
    tag: 'Beach extension',
    summary: 'White-sand recovery after days on safari.',
  },
  {
    href: '#kilimanjaro',
    image: SAFARI_IMAGES.extendedTrip,
    name: 'Kilimanjaro',
    tag: 'Safari + trek',
    summary: 'Combine a Northern Circuit safari with Africa\'s highest peak.',
  },
];

function Safaris() {
  const [heroSlideIndex, setHeroSlideIndex] = useState(0);
  const heroSlide = SAFARI_HERO_SLIDES[heroSlideIndex] ?? SAFARI_HERO_SLIDES[0];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="safaris">
      <section className="safaris-hero" aria-labelledby="safaris-hero-heading">
        <div className="safaris-hero__media" aria-hidden="true">
          <HeroSlideshow
            classPrefix="safaris-hero"
            slides={SAFARI_HERO_SLIDES}
            fallback={IMAGE_FALLBACK}
            onSlideChange={setHeroSlideIndex}
          />
          <div className="safaris-hero__overlay" />
        </div>
        <div className="page-container safaris-hero__container">
          <div className="safaris-hero__content">
            <nav className="safaris-hero__breadcrumb" aria-label="Breadcrumb">
              <a href="/">Home</a>
              <span className="safaris-hero__breadcrumb-sep" aria-hidden="true">›</span>
              <span>Safaris</span>
            </nav>
            <div key={heroSlideIndex} className="safaris-hero__copy">
              <p className="safaris-hero__eyebrow safaris-hero-text-item safaris-hero-text-item--1">
                {heroSlide.eyebrow}
              </p>
              <h1 id="safaris-hero-heading" className="safaris-hero-text-item safaris-hero-text-item--2">
                {heroSlide.title}
                <span> {heroSlide.titleAccent}</span>
              </h1>
              <p className="safaris-hero__lead safaris-hero-text-item safaris-hero-text-item--3">
                {heroSlide.lead}
              </p>
              <div className="safaris-hero__actions safaris-hero-text-item safaris-hero-text-item--4">
                <a className="safaris-btn safaris-btn--primary" href="#contact">
                  Plan your safari
                </a>
                <a className="safaris-btn safaris-btn--outline" href="#safaris-types">
                  Safari experiences
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="safaris-hero__stats">
          {HERO_STATS.map((stat) => (
            <div
              key={stat.label}
              className="safaris-hero__stat"
              style={{ '--stat-delay': `${stat.delay}ms` }}
            >
              <span className="safaris-hero__stat-value">{stat.value}</span>
              <span className="safaris-hero__stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section
        id="safaris-overview"
        className="safaris-section safaris-overview"
        aria-labelledby="overview-heading"
      >
        <div className="page-container">
          <div className="safaris-overview__grid">
            <Reveal className="safaris-overview__text">
              <p className="safaris-section__eyebrow">About Tanzania safaris</p>
              <h2 id="overview-heading">The safari experience, explained</h2>
              <p>
                A Tanzania safari is a journey into some of the last great wilderness
                areas on Earth. With more than 30% of the country set aside for
                conservation, you can track the Big Five, witness the Great Migration,
                and explore landscapes that range from volcanic craters to riverine
                forests all in a single trip.
              </p>
              <p>
                At Mori Tours, every safari is built from scratch. We match parks,
                pacing, and accommodation to your dates, budget, and travel style
                whether you want a compact four-day Northern Circuit or a two-week
                safari-and-beach odyssey across the country.
              </p>
              <ul className="safaris-overview__details">
                <Reveal as="li" className="safaris-overview__detail" delay={0}>
                  <span className="safaris-overview__detail-label">Best season</span>
                  <span className="safaris-overview__detail-value">June – October (dry)</span>
                </Reveal>
                <Reveal as="li" className="safaris-overview__detail" delay={80}>
                  <span className="safaris-overview__detail-label">Gateway city</span>
                  <span className="safaris-overview__detail-value">Arusha &amp; Dar es Salaam</span>
                </Reveal>
                <Reveal as="li" className="safaris-overview__detail" delay={160}>
                  <span className="safaris-overview__detail-label">Group size</span>
                  <span className="safaris-overview__detail-value">Private or small group</span>
                </Reveal>
                <Reveal as="li" className="safaris-overview__detail" delay={240}>
                  <span className="safaris-overview__detail-label">Style</span>
                  <span className="safaris-overview__detail-value">Camping to luxury lodge</span>
                </Reveal>
              </ul>
            </Reveal>
            <Reveal className="safaris-overview__image" variant="right" delay={200}>
              <SafeImage
                className="safaris-overview__img"
                src={SAFARI_IMAGES.overview}
                alt="Lions resting in golden Serengeti savanna during a game drive"
              />
              <span className="safaris-overview__image-accent" aria-hidden="true" />
            </Reveal>
          </div>
        </div>
      </section>

      <section
        id="safaris-types"
        className="safaris-section safaris-types"
        aria-labelledby="types-heading"
      >
        <div className="page-container">
          <Reveal className="safaris-section__header">
            <p className="safaris-section__eyebrow">Safari experiences</p>
            <h2 id="types-heading">Ways to explore the wild</h2>
            <p className="safaris-section__intro">
              Every safari day is different. Mix classic game drives with balloon
              flights, walking safaris, and cultural visits we design the balance
              around what excites you most.
            </p>
          </Reveal>
          <ul className="safaris-types__grid">
            {SAFARI_TYPES.map((item, index) => (
              <Reveal
                key={item.title}
                as="li"
                className="safaris-types__card"
                delay={index * 80}
              >
                <div className="safaris-types__card-image">
                  <SafeImage src={item.image} alt={item.title} />
                </div>
                <div className="safaris-types__card-body">
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="safaris-circuits"
        className="safaris-section safaris-circuits"
        aria-labelledby="circuits-heading"
      >
        <div className="page-container">
          <Reveal className="safaris-section__header safaris-section__header--light">
            <p className="safaris-section__eyebrow safaris-section__eyebrow--light">Safari circuits</p>
            <h2 id="circuits-heading">Three regions, endless adventure</h2>
            <p className="safaris-section__intro safaris-section__intro--light">
              Tanzania&apos;s parks are grouped into distinct circuits each with its
              own character, wildlife, and travel logistics. Most visitors start on
              the Northern Circuit, but the south and west reward those who venture further.
            </p>
          </Reveal>
          <ul className="safaris-circuits__grid">
            {SAFARI_CIRCUITS.map((circuit, index) => (
              <Reveal
                key={circuit.title}
                as="li"
                className="safaris-circuits__card"
                delay={index * 120}
              >
                <div className="safaris-circuits__card-image">
                  <SafeImage src={circuit.image} alt={circuit.title} />
                  <span className="safaris-circuits__badge">{circuit.badge}</span>
                </div>
                <div className="safaris-circuits__card-body">
                  <h3>{circuit.title}</h3>
                  <p>{circuit.text}</p>
                  {circuit.parks.length > 0 && (
                    <ul className="safaris-circuits__links">
                      {circuit.parks.map((park) => (
                        <li key={park.href}>
                          <a href={park.href}>{park.label} →</a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="safaris-durations"
        className="safaris-section safaris-durations"
        aria-labelledby="durations-heading"
      >
        <div className="page-container">
          <Reveal className="safaris-section__header">
            <p className="safaris-section__eyebrow">Trip length</p>
            <h2 id="durations-heading">How long should you safari?</h2>
            <p className="safaris-section__intro">
              There is no single right answer but these three frameworks help most
              travellers find the sweet spot between time, budget, and depth of experience.
            </p>
          </Reveal>
          <ul className="safaris-durations__grid">
            {TRIP_DURATIONS.map((trip, index) => (
              <Reveal
                key={trip.title}
                as="li"
                className="safaris-durations__card"
                delay={index * 120}
              >
                <div className="safaris-durations__card-image">
                  <SafeImage src={trip.image} alt={trip.title} />
                  <span className="safaris-durations__duration">{trip.duration}</span>
                </div>
                <div className="safaris-durations__card-body">
                  <h3>{trip.title}</h3>
                  <p>{trip.text}</p>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="safaris-included"
        className="safaris-section safaris-included"
        aria-labelledby="included-heading"
      >
        <div className="page-container">
          <div className="safaris-included__inner">
            <Reveal className="safaris-included__copy">
              <p className="safaris-section__eyebrow">What&apos;s included</p>
              <h2 id="included-heading">Your safari, fully supported</h2>
              <p>
                We handle the logistics so you can focus on the wildlife. Every Mori
                Tours safari quote is transparent no hidden park fees or surprise
                add-ons on arrival.
              </p>
              <ul className="safaris-included__list">
                {INCLUDED_ITEMS.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal className="safaris-included__aside" variant="right" delay={200}>
              <div className="safaris-included__panel">
                <p className="safaris-included__panel-eyebrow">How it works</p>
                <ol className="safaris-included__steps">
                  <li>
                    <strong>Tell us your dates</strong>
                    <span>Share travel window, group size, and interests.</span>
                  </li>
                  <li>
                    <strong>We design your route</strong>
                    <span>Day-by-day plan with parks, drives, and lodge options.</span>
                  </li>
                  <li>
                    <strong>Refine &amp; confirm</strong>
                    <span>Adjust until it feels right, then we book everything.</span>
                  </li>
                  <li>
                    <strong>Arrive &amp; explore</strong>
                    <span>Your guide meets you at the airport adventure begins.</span>
                  </li>
                </ol>
                <a className="safaris-btn safaris-btn--dark" href="#contact">
                  Request a free quote
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section
        id="safaris-destinations"
        className="safaris-section safaris-destinations"
        aria-labelledby="destinations-heading"
      >
        <div className="page-container">
          <Reveal className="safaris-section__header">
            <p className="safaris-section__eyebrow">Where to go</p>
            <h2 id="destinations-heading">Featured safari destinations</h2>
            <p className="safaris-section__intro">
              Explore our destination guides for park-specific wildlife, seasons,
              and travel tips then let us stitch them into your perfect itinerary.
            </p>
          </Reveal>
          <ul className="safaris-destinations__grid">
            {FEATURED_DESTINATIONS.map((dest, index) => (
              <Reveal
                key={dest.href}
                as="li"
                className="safaris-destinations__card"
                delay={index * 80}
              >
                <a href={dest.href} className="safaris-destinations__link">
                  <div className="safaris-destinations__image">
                    <SafeImage src={dest.image} alt={dest.name} />
                    <span className="safaris-destinations__tag">{dest.tag}</span>
                  </div>
                  <div className="safaris-destinations__body">
                    <h3>{dest.name}</h3>
                    <p>{dest.summary}</p>
                    <span className="safaris-destinations__cta">View destination →</span>
                  </div>
                </a>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className="safaris-cta" aria-labelledby="safaris-cta-heading">
        <div className="page-container">
          <Reveal variant="scale" delay={0}>
            <div className="safaris-cta__inner">
              <h2 id="safaris-cta-heading">Ready to plan your Tanzania safari?</h2>
              <p>
                Share your travel dates and dream parks we&apos;ll send a
                tailor-made itinerary and quote within 24 hours.
              </p>
              <a className="safaris-btn safaris-btn--primary safaris-btn--lg" href="#contact">
                Get a free safari quote
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

export default Safaris;
